import { Timestamp } from "firebase/firestore";
import { ManualTrade } from "../types";
import { GenevaRequest } from "../types/geneva-schema";

function toGenevaDate(ts: Timestamp | null | undefined): string | undefined {
    if (!ts) return undefined;

    const date = ts.toDate();
    // Adjust for local timezone (GMT+3)
    const offsetMs = 3 * 60 * 60 * 1000;
    const localDate = new Date(date.getTime() + offsetMs);

    return localDate.toISOString().split(".")[0];
}

function getManualTradePath(trade: ManualTrade): string {
    if (!trade.tradeDate || !trade.id) {
        throw new Error("tradeDate and id are required to build the path");
    }

    const date: Date = trade.tradeDate.toDate();
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    const formattedDate = `${day}-${month}-${year}`;

    return `/trade_dates/${formattedDate}/manual_trades/${trade.id}`;
}

// Wakala currency is derived from the fund: 901 is KWD, 902 is USD
function deriveWakalaCurrency(portfolioId: string): 'USD' | 'KWD' {
    return portfolioId === '902' ? 'USD' : 'KWD';
}

export const convertManualTradeToGeneva = (
    trade: ManualTrade
): GenevaRequest => {
    const sukuk = trade.sukuk;
    const wakala = trade.moneyMarket;
    const hasSukukData = !!sukuk && !trade.selectedSukukId;
    const hasWakalaData = !!wakala;
    const ExternalSystemReferenceID = getManualTradePath(trade);

    const isSukukTrade = trade.type === 'sukuk';
    const isMoneyMarketTrade = trade.type === 'moneyMarket';

    const sukukCurrency = sukuk?._PRINCIPAL_CURRENCY_CODE_ || "USD";
    const eventDateStr = toGenevaDate(trade.tradeDate) ?? null;
    const settleDateStr = toGenevaDate(trade.settlementDate ?? trade.tradeDate) ?? null;

    let transactionType = '';
    let formData: Record<string, any> = {};

    // --- Case 1: Buy & Create new Sukuk ---
    if (isSukukTrade && trade.transactionType === "BUY" && hasSukukData) {
        transactionType = 'MultiRecord_BuyNewSukukorMM';
        // Never fall back to the Firestore doc id: Geneva bond codes must come from
        // the counters/bond sequence assigned by the manualTradeCreated/Updated trigger.
        if (!sukuk?.CODE) {
            throw new Error("Sukuk has no CODE yet. Wait for the trigger or re-save the trade before sending to Geneva.");
        }
        const bondCode = sukuk.CODE;
        formData = {
            _multiRecord: true,
            _virtualRecordId: "MultiRecord_BuyNewSukukorMM",
            records: {
                MultiRecord_BuyNewSukukorMM__Bond: {
                    Code: bondCode,
                    Description: sukuk?.DESCRIPTION ?? "",
                    PricingFactor: 0.01,
                    TradingFactor: 1.0,
                    CurrentPriceDayRange: 365,
                    SettleDays: 0,
                    QuantityPrecision: 0,
                    IssueCountry: sukuk?.ISSUE_COUNTRY_CODE ?? "",
                    BifurcationCurrency: sukukCurrency,
                    RiskCurrency: sukukCurrency,
                    IncomeCurrency: sukukCurrency,
                    PrincipalCurrency: sukukCurrency,
                    PriceDenomination: sukukCurrency,
                    Exchange: "EURCLE",
                    AssetType: "SK",
                    InvestmentType: "SK",
                    Issuer: sukuk?._ISSUER_NAMESORT_?.trim() ?? "",  // trimmed like convertNewIssuerToGeneva's NameSort
                    SubIndustry: sukuk?._UDF_SECTOR_ ?? "",
                    Coupon: sukuk?.COUPON ?? "",
                    YieldMethod: "UseDefault",
                    MaturityPrice: 100,
                    IssuePrice: 100,
                    IssueDate: toGenevaDate(sukuk?.ISSUEDATE) ?? null,
                    DatedDate: toGenevaDate(sukuk?.ISSUEDATE) ?? null,
                    MaturityDate: toGenevaDate(sukuk?.MATURITYDATE) ?? null,
                    FirstCouponDate: toGenevaDate(sukuk?.FIRSTCOUPONDATE) ?? null,
                    CouponFreq: sukuk?.COUPON_FREQUENCY ?? "",
                    SimpleInterestFlag: "SimpleInterest",
                    EndOfMonthTips: "Adjust",
                    CouponDelayMethod: "NumCalendarDays",
                    RecordIntervalMethod: "NumCalendarDays",
                    AccrualFactorPrecision: 0,
                    AccrualDaysPerYear: 360,
                    AccrualDaysPerMonth: "30E",
                    AccrualMethod: "Simple",
                    Isin: sukuk?.ISIN ?? "",
                    BloombergID: sukuk?.BLOOMBERGID ?? "",
                    BloombergMarketSector: sukuk?.BLOOMBERGMARKETSECTOR ?? "",
                    SharesOutstanding: sukuk?.SHARESOUTSTANDING ?? "",
                    KeyValue: bondCode,
                    ExternalSystemReferenceID: `${ExternalSystemReferenceID}:new_sukuk`,
                },
                MultiRecord_BuyNewSukukorMM__Buy: {
                    Portfolio: trade.portfolioId,
                    LocationAccount: trade.custodianAccountId ?? "",
                    EventDate: eventDateStr,
                    SettleDate: settleDateStr,
                    ActualSettleDate: settleDateStr,
                    Quantity: trade.nominalValue,
                    Price: trade.pricePerUnit,
                    PriceDenomination: sukukCurrency,
                    CounterInvestment: sukukCurrency,
                    NetCounterAmount: "CALC",
                    NetTradeFlag: 0,
                    AccrueCommissionFlag: 0,
                    AccruedInterest: "CALC",
                    InvestmentAccruedInterest: "CALC",
                    tradeFX: 1,
                    Broker: "EC",
                    UserTranId1: `manual-buy-${trade.id ?? ""}`,
                    Comments: trade.remarks ?? "",
                    KeyValue: `${ExternalSystemReferenceID}:buy_sukuk`,
                    ExternalSystemReferenceID: `${ExternalSystemReferenceID}:buy_sukuk`,
                    ...(trade.fundStructure ? { FundStructure: trade.fundStructure } : {})
                },
            },
        };
    }

    // --- Case 2: Buy Existing Sukuk ---
    if (isSukukTrade && trade.transactionType === "BUY" && trade.selectedSukukId) {
        transactionType = 'Buy_BuySukuk';
        formData = {
            Buy_BuySukuk: {
                Portfolio: trade.portfolioId,
                LocationAccount: trade.custodianAccountId ?? "",
                Investment: trade.selectedSukukId,
                EventDate: eventDateStr,
                SettleDate: settleDateStr,
                ActualSettleDate: settleDateStr,
                Quantity: trade.nominalValue,
                Price: trade.pricePerUnit,
                PriceDenomination: sukukCurrency,
                CounterInvestment: sukukCurrency,
                NetCounterAmount: "CALC",
                NetTradeFlag: 0,
                AccrueCommissionFlag: 0,
                AccruedInterest: "CALC",
                InvestmentAccruedInterest: "CALC",
                tradeFX: 1,
                Broker: "EC",
                UserTranId1: `manual-buy-${trade.id ?? ""}`,
                Comments: trade.remarks ?? "",
                KeyValue: `${ExternalSystemReferenceID}:buy_sukuk`,
                ExternalSystemReferenceID: `${ExternalSystemReferenceID}:buy_sukuk`,
                ...(trade.fundStructure ? { FundStructure: trade.fundStructure } : {})
            },
        };
    }

    // --- Case 3: Sell Existing Sukuk ---
    if (isSukukTrade && trade.transactionType === "SELL" && trade.selectedSukukId) {
        transactionType = 'Sell_SellSukuk';
        formData = {
            Sell_SellSukuk: {
                Portfolio: trade.portfolioId,
                LocationAccount: trade.custodianAccountId ?? "",
                Investment: trade.selectedSukukId,
                EventDate: eventDateStr,
                SettleDate: settleDateStr,
                ActualSettleDate: settleDateStr,
                Quantity: trade.nominalValue,
                Price: trade.pricePerUnit,
                PriceDenomination: sukukCurrency,
                CounterInvestment: sukukCurrency,
                NetCounterAmount: "CALC",
                NetTradeFlag: 0,
                AccrueCommissionFlag: 0,
                AccruedInterest: "CALC",
                InvestmentAccruedInterest: "CALC",
                tradeFX: 1,
                Broker: "EC",
                UserTranId1: `manual-sell-${trade.id ?? ""}`,
                Comments: trade.remarks ?? "",
                KeyValue: `${ExternalSystemReferenceID}:sell_sukuk`,
                ExternalSystemReferenceID: `${ExternalSystemReferenceID}:sell_sukuk`,
                ...(trade.fundStructure ? { FundStructure: trade.fundStructure } : {})
            },
        };
    }

    // --- Case 4: Buy & Create new Wakala (Money Market) ---
    if (isMoneyMarketTrade && trade.transactionType === "BUY" && hasWakalaData) {
        transactionType = 'MultiRecord_BuyNewWakala';
        const wakalaCurrency = deriveWakalaCurrency(trade.portfolioId);
        if (!wakala?.id || wakala.id === 0) {
            throw new Error("Money market instrument has no id yet. Wait for the trigger or re-save the trade before sending to Geneva.");
        }
        const wakalaCode = String(wakala.id);
        const wakalaIssueDate = toGenevaDate(wakala?.issueDate) ?? null;
        const wakalaMaturityDate = toGenevaDate(wakala?.maturityDate) ?? null;

        formData = {
            _multiRecord: true,
            _virtualRecordId: "MultiRecord_BuyNewWakala",
            records: {
                MultiRecord_BuyNewWakala__Bond: {
                    Code: wakalaCode,
                    Description: wakala?.description ?? "",
                    PricingFactor: 0.01,
                    TradingFactor: 1.0,
                    CurrentPriceDayRange: 365,
                    SettleDays: 0,
                    QuantityPrecision: 0,
                    IssueCountry: wakala?.issuerCountry ?? "",
                    BifurcationCurrency: wakalaCurrency,
                    RiskCurrency: wakalaCurrency,
                    IncomeCurrency: wakalaCurrency,
                    PrincipalCurrency: wakalaCurrency,
                    PriceDenomination: wakalaCurrency,
                    Exchange: "OTC",
                    AssetType: "FI",
                    InvestmentType: "WK",
                    Issuer: wakala?.issuer ?? "",
                    Coupon: wakala?.rate ?? "",
                    YieldMethod: "UseDefault",
                    MaturityPrice: 100,
                    IssuePrice: 100,
                    IssueDate: wakalaIssueDate,
                    DatedDate: wakalaIssueDate,
                    MaturityDate: wakalaMaturityDate,
                    // First coupon always equals maturity for money market (annual at maturity)
                    FirstCouponDate: wakalaMaturityDate,
                    CouponFreq: "Annual",
                    SimpleInterestFlag: "SimpleInterest",
                    EndOfMonthTips: "Adjust",
                    CouponDelayMethod: "NumCalendarDays",
                    RecordIntervalMethod: "NumCalendarDays",
                    AccrualFactorPrecision: 0,
                    AccrualDaysPerYear: wakalaCurrency === 'USD' ? 360 : 365,
                    AccrualMethod: "Simple",
                    KeyValue: wakalaCode,
                    ExternalSystemReferenceID: `${ExternalSystemReferenceID}:new_wakala`,
                },
                MultiRecord_BuyNewWakala__Buy: {
                    Portfolio: trade.portfolioId,
                    LocationAccount: trade.custodianAccountId ?? "",
                    Investment: wakalaCode,
                    EventDate: eventDateStr,
                    SettleDate: settleDateStr,
                    ActualSettleDate: settleDateStr,
                    InvestmentClosingRule: "NULL",
                    Quantity: trade.nominalValue,
                    Price: 100,
                    NetCounterAmount: "CALC",
                    NetTradeFlag: 0,
                    AccrueCommissionFlag: 0,
                    PriceDenomination: wakalaCurrency,
                    CounterFXDenomination: wakalaCurrency,
                    CounterInvestment: wakalaCurrency,
                    CounterTDateFx: 0,
                    CounterSDateFx: 0,
                    AccruedInterest: "CALC",
                    NetInvestmentAmount: "CALC",
                    Broker: "KFHB",
                    tradeFX: 1,
                    FundStructure: trade.fundStructure ?? trade.portfolioId,
                    UserTranId1: `manual-buy-wakala-${trade.id ?? ""}`,
                    Comments: trade.remarks ?? "",
                    KeyValue: `${ExternalSystemReferenceID}:buy_wakala`,
                    ExternalSystemReferenceID: `${ExternalSystemReferenceID}:buy_wakala`,
                },
            },
        };
    }

    if (!transactionType || transactionType.trim() === "") {
        throw new Error(
            "Unable to determine transactionType for GenevaRequest. Please check trade data and conditions."
        );
    }

    const metadata: GenevaRequest["metadata"] = {
        recordType: transactionType,
        isMultiRecord: !!formData._multiRecord,
        fieldCount: Object.keys(formData).length,
        arrayFieldCount: 0,
        ...(formData._virtualRecordId ? { virtualRecordId: formData._virtualRecordId } : {}),
    };

    const genevaRequest: GenevaRequest = {
        id: trade.id ?? "",
        orgId: "demo", // TODO: replace dynamically
        templateId: "default", // TODO: replace dynamically
        status: "draft",
        schemaType: "geneva",
        formData,
        metadata,
        audit: {
            createdAt: Date.now(),
            createdBy: trade.createdById ?? "",
            createdByEmail: "",
            createdByName: trade.createdBy ?? "",
        },
        processing: {},
    };

    return genevaRequest;
};

// New Issuer (party) for a new sukuk whose LSEG issuer has no Geneva match.
// Sent on its own, before the buy: Issuer can't join MultiRecord_BuyNewSukukorMM
// (multi-record validation only checks the first record, and records are written
// in config order). ExternalSystemReferenceID has no ":suffix", so the status
// lands on the trade's `geneva_response` (singular) — outside geneva_responses,
// so it never affects the trade's LOCKED/GENEVA_ERROR state or gets archived.
// The request id is the name because the generator stamps it as KeyValue, and a
// party's KeyValue is its NameSort: InsertUpdate then updates the issuer if it
// already exists (re-send, a second trade on the same issuer) instead of failing.
// Safe as an id: the SOAP loader path never uses it beyond logging.
export const convertNewIssuerToGeneva = (trade: ManualTrade): GenevaRequest => {
    const name = trade.sukuk?._ISSUER_NAMESORT_?.trim();
    if (!name) throw new Error("New issuer has no name.");
    const formData = {
        NameSort: name,
        NameLine1: name,
        ExternalSystemReferenceID: getManualTradePath(trade),
    };
    return {
        id: name,
        orgId: "demo", // TODO: replace dynamically
        templateId: "default", // TODO: replace dynamically
        status: "draft",
        schemaType: "geneva",
        formData,
        metadata: { recordType: "Issuer", isMultiRecord: false, fieldCount: Object.keys(formData).length, arrayFieldCount: 0 },
        audit: {
            createdAt: Date.now(),
            createdBy: trade.createdById ?? "",
            createdByEmail: "",
            createdByName: trade.createdBy ?? "",
        },
        processing: {},
    };
};
